/* Admin School Holidays — minimal CRUD page.
   Teachers can view holidays read-only via /api/teacher/school-holidays
   (rendered inside the calendar). This page lets admin add / edit /
   delete / bulk-add Yamaha school holidays. */
import { useState } from "react";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PartyPopper, CalendarRange } from "lucide-react";

const TYPE_OPTIONS = [
  { v: "yamaha_school_holiday", label: "Yamaha School Holiday", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  { v: "public_holiday", label: "Public Holiday", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { v: "school_closure", label: "School Closure", cls: "bg-zinc-100 text-zinc-800 border-zinc-200" },
  { v: "annual_concert", label: "Annual Concert", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  { v: "custom", label: "Custom", cls: "bg-blue-100 text-blue-800 border-blue-200" },
];
const typeStyle = (t) => TYPE_OPTIONS.find((o) => o.v === t) || TYPE_OPTIONS[4];

function HolidayDialog({ open, onOpenChange, editing, onSaved }) {
  const isEdit = !!editing?.id;
  const [date, setDate] = useState(editing?.date || "");
  const [label, setLabel] = useState(editing?.label || "");
  const [type, setType] = useState(editing?.holiday_type || "yamaha_school_holiday");
  const [note, setNote] = useState(editing?.note || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!date || !label) { toast.error("Date and label are required"); return; }
    setBusy(true);
    try {
      const body = { date, label, holiday_type: type, note: note || null,
                     applies_to_all: true, is_observed: true };
      if (isEdit) await api.put(`/admin/school-holidays/${editing.id}`, body);
      else await api.post("/admin/school-holidays", body);
      toast.success(isEdit ? "Holiday updated" : "Holiday added");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="holiday-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit holiday" : "Add holiday"}</DialogTitle>
          <DialogDescription>
            Set when the school is closed. This appears in every teacher's calendar.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                     data-testid="holiday-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <select value={type} onChange={(e) => setType(e.target.value)}
                      data-testid="holiday-type"
                      className="w-full border rounded-md px-3 py-2 text-sm bg-card">
                {TYPE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="e.g. Year-end break" data-testid="holiday-label" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      data-testid="holiday-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="holiday-save">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add holiday"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkDialog({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [type, setType] = useState("yamaha_school_holiday");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name || !start || !end) { toast.error("Name, start and end are required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/school-holidays/bulk", {
        name, start_date: start, end_date: end, holiday_type: type,
      });
      toast.success(`Added ${r.data.inserted} holiday day${r.data.inserted === 1 ? "" : "s"}`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle>Add holiday range</DialogTitle>
          <DialogDescription>
            For multi-day Yamaha school breaks. We&apos;ll create one row per day.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Mid-year Yamaha break" data-testid="bulk-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Start date</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                     data-testid="bulk-start" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">End date</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                     data-testid="bulk-end" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)}
                    data-testid="bulk-type"
                    className="w-full border rounded-md px-3 py-2 text-sm bg-card">
              {TYPE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="bulk-save">
            {busy ? "Adding…" : "Add range"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SchoolHolidaysAdminPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, loading, reload } = useList(`/admin/school-holidays?year=${year}`, [year]);
  const [single, setSingle] = useState(null);
  const [bulk, setBulk] = useState(false);

  if (loading) return <Spinner full />;
  const items = data || [];

  // Group by month
  const byMonth = items.reduce((acc, h) => {
    const m = h.date.slice(0, 7);
    (acc[m] = acc[m] || []).push(h);
    return acc;
  }, {});

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.label}" on ${h.date}?`)) return;
    try {
      await api.delete(`/admin/school-holidays/${h.id}`);
      toast.success("Holiday removed");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="school-holidays-page">
      <PageHeader title="School holidays"
        description="Manage Yamaha school holidays, public holidays and school closures."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
                   className="w-24" data-testid="year-input" />
            <Button variant="outline" size="sm" onClick={() => setBulk(true)} data-testid="add-range-btn">
              <CalendarRange className="h-4 w-4 mr-1" />Add range
            </Button>
            <Button size="sm" onClick={() => setSingle({})} data-testid="add-holiday-btn">
              <Plus className="h-4 w-4 mr-1" />Add holiday
            </Button>
          </div>
        } />

      {items.length === 0 ? (
        <EmptyState text="No holidays configured for this year. Use 'Add holiday' or 'Add range' to start." />
      ) : (
        <div className="space-y-5">
          {Object.entries(byMonth).map(([m, rows]) => (
            <div key={m} className="rounded-lg border bg-card overflow-hidden" data-testid={`holidays-month-${m}`}>
              <div className="px-4 py-2 bg-muted/30 border-b text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {new Date(m + "-01").toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                {" · "}<span className="text-foreground">{rows.length} day{rows.length === 1 ? "" : "s"}</span>
              </div>
              <div className="divide-y">
                {rows.map((h) => {
                  const t = typeStyle(h.holiday_type);
                  return (
                    <div key={h.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5"
                         data-testid={`holiday-row-${h.id}`}>
                      <PartyPopper className="h-4 w-4 text-rose-600 shrink-0" />
                      <span className="font-mono text-xs w-28 shrink-0">{h.date}</span>
                      <span className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{h.label}</p>
                        {h.note && <p className="text-xs text-muted-foreground truncate">{h.note}</p>}
                      </span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${t.cls}`}>
                        {t.label}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => setSingle(h)}
                              data-testid={`edit-holiday-${h.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(h)}
                              data-testid={`delete-holiday-${h.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {single !== null && (
        <HolidayDialog open={true} onOpenChange={(o) => !o && setSingle(null)}
                       editing={single} onSaved={reload} />
      )}
      <BulkDialog open={bulk} onOpenChange={setBulk} onSaved={reload} />
    </div>
  );
}
